import React, { useEffect, useState } from "react";
import sh from "bundles/common/utils/sh";
import PipelinePickerDialog from "bundles/Internal/components/common/PipelinePickerDialog";
import ConfirmDialog from "bundles/Internal/components/common/ConfirmDialog";

const candidateLabel = (candidate) => {
  if (!candidate) return "this candidate";
  const name = `${candidate.first_name || ""} ${candidate.last_name || ""}`.trim();
  return name || candidate.email || "this candidate";
};

const duplicateDescription = (existing, pipeline) => {
  if (!existing) return "";
  const name = `${existing.first_name || ""} ${existing.last_name || ""}`.trim() || existing.email || "this person";
  const detail = existing.email || existing.phone;
  const pipelineName = pipeline?.title || "this pipeline";
  return `A profile already exists for ${name}${detail ? ` (${detail})` : ""}. Link the existing profile to “${pipelineName}”?`;
};

const AddCandidateToPipelineFlow = ({
  open,
  onClose,
  onAdded,
  candidate,
  authenticityToken,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedPipelines, setSelectedPipelines] = useState([]);
  const [duplicatePipeline, setDuplicatePipeline] = useState(null);
  const [duplicate, setDuplicate] = useState(null);
  const [pendingPipelines, setPendingPipelines] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setPickerOpen(true);
      setSelectedPipelines([]);
      setDuplicatePipeline(null);
      setDuplicate(null);
      setPendingPipelines([]);
      setSubmitting(false);
    } else {
      setPickerOpen(false);
      setSelectedPipelines([]);
      setDuplicatePipeline(null);
      setDuplicate(null);
      setPendingPipelines([]);
      setSubmitting(false);
    }
  }, [open]);

  const buildFormData = ({ confirmDuplicate = false } = {}) => {
    const fd = new FormData();
    fd.append("authenticity_token", authenticityToken || "");
    fd.append("external_profile[candidate_id]", candidate?.id || "");
    if (confirmDuplicate) fd.append("confirm_duplicate", "true");
    return fd;
  };

  const post = (pipeline, { confirmDuplicate = false } = {}) =>
    sh.post(
      `/internal/profile_pipelines/${pipeline.id}/external_profiles`,
      buildFormData({ confirmDuplicate }),
      { headers: { "Content-Type": "multipart/form-data" } },
    );

  const processQueue = async (queue) => {
    for (const pipeline of queue) {
      try {
        const res = await post(pipeline);
        const wasCreated = res?.status === 201;
        onAdded?.({ ...res?.data, wasCreated }, pipeline);
      } catch (err) {
        if (err?.response?.status === 409 && err.response.data?.existing_profile) {
          const remaining = queue.slice(queue.indexOf(pipeline) + 1);
          setPendingPipelines(remaining);
          setDuplicatePipeline(pipeline);
          setDuplicate(err.response.data.existing_profile);
          setPickerOpen(false);
          return;
        }
        const message = err?.response?.data?.errors?.join(", ") || `Could not add to "${pipeline.title || "pipeline"}".`;
        // eslint-disable-next-line no-alert
        window.alert(message);
      }
    }
    onClose?.();
  };

  const handlePickerConfirm = (pipelines) => {
    if (submitting || !candidate?.id) return;
    setSelectedPipelines(pipelines);
    setSubmitting(true);
    processQueue(pipelines).finally(() => setSubmitting(false));
  };

  const handleDuplicateConfirm = () => {
    if (submitting || !duplicatePipeline) return;
    setSubmitting(true);
    post(duplicatePipeline, { confirmDuplicate: true })
      .then((res) => {
        const wasCreated = res?.status === 201;
        onAdded?.({ ...res?.data, wasCreated }, duplicatePipeline);
        setDuplicate(null);
        setDuplicatePipeline(null);
        if (pendingPipelines.length > 0) {
          const remaining = [...pendingPipelines];
          setPendingPipelines([]);
          processQueue(remaining).finally(() => setSubmitting(false));
        } else {
          setSubmitting(false);
          onClose?.();
        }
      })
      .catch((err) => {
        const message = err?.response?.data?.errors?.join(", ") || "Could not link existing profile.";
        // eslint-disable-next-line no-alert
        window.alert(message);
        setDuplicate(null);
        setDuplicatePipeline(null);
        if (pendingPipelines.length > 0) {
          const remaining = [...pendingPipelines];
          setPendingPipelines([]);
          processQueue(remaining).finally(() => setSubmitting(false));
        } else {
          setSubmitting(false);
          onClose?.();
        }
      });
  };

  const handleDuplicateCancel = () => {
    if (submitting) return;
    setDuplicate(null);
    setDuplicatePipeline(null);
    if (pendingPipelines.length > 0) {
      const remaining = [...pendingPipelines];
      setPendingPipelines([]);
      setSubmitting(true);
      processQueue(remaining).finally(() => setSubmitting(false));
    } else {
      onClose?.();
    }
  };

  return (
    <>
      <PipelinePickerDialog
        open={open && pickerOpen && !duplicate}
        onClose={() => {
          if (submitting) return;
          onClose?.();
        }}
        onConfirm={handlePickerConfirm}
        title={`Add ${candidateLabel(candidate)} to pipelines`}
        confirmLabel="Add to pipelines"
        loading={submitting}
      />
      <ConfirmDialog
        open={open && !!duplicate}
        onClose={handleDuplicateCancel}
        onConfirm={handleDuplicateConfirm}
        title="Profile already exists"
        description={duplicateDescription(duplicate, duplicatePipeline)}
        confirmLabel={submitting ? "Linking…" : "Link to pipeline"}
        cancelLabel="Skip"
      />
    </>
  );
};

export default AddCandidateToPipelineFlow;
